<template>
  <div>
    <h1 class="text-2xl mb-6">
      Création d'articles à partir de mots-clés
      <dag-info :dags="page.dags" />
    </h1>

    <v-card class="mb-6 pa-2" outlined>
      <v-row class="ma-3">
        <v-col cols="12">
          <filters></filters>
        </v-col>
      </v-row>
      <v-row class="ma-3 mt-0">
        <v-col cols="12" class="py-0">
          <v-checkbox
            v-model="page.forceRefresh"
            dense
            hide-details
            class="mt-0"
            label="Ignorer le cache"
            persistent-hint
            hint="Relance un vrai appel au modèle au lieu de resservir la réponse mise en cache 24h : à cocher pour obtenir des résultats différents sur les mêmes mots-clés."
          />
        </v-col>
      </v-row>
    </v-card>

    <template v-for="[module, data] of Object.entries(page.data)">
      <v-row v-if="data.length > 0" :key="module">
        <v-col cols="12">
          <results
            :title="BLOCK_TITLES[module]"
            :module="module"
            :data="data"
            @valueChanged="setData"
          />
        </v-col>
      </v-row>
    </template>

    <v-row v-if="submitData.length > 0" class="pb-6">
      <v-col cols="12">
        <v-card outlined class="pa-4">
          <v-row justify="space-between">
            <v-col cols="12" md="4">
              <custom-prompt-template-filter
                :noUrl="true"
                :forceSiteId="selectedSiteId"
                store="aiFactory"
                preselect-default
                dense
              />
            </v-col>
            <v-col cols="12" md="3">
              <aif-image-mode-toggle
                v-model="imageHandler"
                layout="select"
                label="Image"
              />
            </v-col>
            <v-col cols="12" md="auto" class="text-right">
              <v-btn
                color="primary"
                class="mt-3 mt-md-0"
                :disabled="buttonDisabled"
                @click.native="submit"
              >
                {{
                  submitData.length > 1
                    ? "Générer les brouillons"
                    : "Générer le brouillon"
                }}</v-btn
              >
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <progress-bar
      title="🤖 Votre agent IA est sur le coup..."
      :showDownloadState="false"
      :download="download"
      :steps="page.submitting ? stepsSubmit : stepsGetData"
    />

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="-1"
      top
      class="snackbar-center"
    >
      <div>
        <strong>{{ snackbar.message }}</strong>
        <!-- Optionnel : afficher les topics détaillés -->
        <ul v-if="snackbar.details && snackbar.details.length">
          <li v-for="item in snackbar.details" :key="item.topic">
            {{ item.topic }} :
            {{ item.status === "success" ? "✅ OK" : "❌ Erreur" }}
            <span v-if="item.error"> — {{ item.error }}</span>
          </li>
        </ul>
      </div>

      <template v-slot:action>
        <v-btn
          v-for="action in snackbar.actions"
          :key="action.text"
          color="primary"
          dark
          large
          elevation="2"
          class="ma-2"
          @click="action.onClick()"
        >
          ✅ {{ action.text }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {
  computed,
  onMounted,
  reactive,
  watch,
  set,
} from "@vue/composition-api";

import DagInfo from "@/components/common/menus/DagInfo";
import ProgressBar, {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar.vue";
import AifImageModeToggle from "@/components/ai-factory/common/AifImageModeToggle.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import Filters from "@/components/ai-factory/post-by-keywords/filters/Filters.vue";
import Results from "@/components/ai-factory/common/tables/KwAngleResults.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { NEWS_RADAR_CRAWLER_DAGS } from "@/utils/constants";
import { saveRmraView } from "@/utils/rmr-analytics";

// BLOCK_TITLES will be dynamically generated from thematiques
const BLOCK_TITLES = reactive({});

const THEMATIC_EMOJIS = {
  "Argent & Consommation": "💰",
  "Auto & Transports": "🚗",
  "Santé & Bien-être": "🏥",
  "Maison & Jardin": "🏠",
  "Tech & Photo": "📰",
  "Nature & Science": "🔬",
  Autre: "📚",
};

export default {
  name: "GeneratePostByKeywords",
  components: {
    AifImageModeToggle,
    CustomPromptTemplateFilter,
    DagInfo,
    Filters,
    ProgressBar,
    Results,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost } = useAxios();
    const { route, store, router } = useProxy();

    const snackbar = reactive({
      show: false,
      message: "",
      color: "success",
      actions: [],
      details: [],
    });

    const defaultData = reactive({});

    const page = reactive({
      loading: false,
      submitting: false,
      dags: NEWS_RADAR_CRAWLER_DAGS,
      data: {},
      selectedData: {},
      forceRefresh: false,
    });

    // Le back renvoie sa cause exacte dans `detail` (ex: "Stage 2 Error: ...").
    // On la lit defensivement : sur une erreur reseau il n'y a pas de reponse
    // du tout, et un acces direct a error.response.data ferait planter le catch.
    const getErrorDetail = (error) => {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") return detail;
      if (detail) return JSON.stringify(detail);
      return error?.message || "Erreur inconnue";
    };

    const download = reactive(download_initial_state());

    // =>  METHODS
    const formatData = (data) => {
      const result = {};
      const thematiqueTitles = {};

      if (data && data.thematiques && data.thematiques.length > 0) {
        data.thematiques.forEach((thematique) => {
          const moduleKey = `thematique_${thematique.thematique.replace(
            /\s+/g,
            "_"
          )}`;
          thematiqueTitles[moduleKey] = thematique.thematique;

          if (thematique.angles && thematique.angles.length > 0) {
            result[moduleKey] = thematique.angles.map((angle) => {
              return {
                angle: angle.angle,
                titre_discover: angle.titre_discover,
                preuve: angle.preuve,
                potentiel_discover: angle.potentiel_discover,
                source_url: angle.source_url || "",
              };
            });
          }
        });
      }

      Object.keys(thematiqueTitles).forEach((key) => {
        const thematiqueName = thematiqueTitles[key];
        const emoji = THEMATIC_EMOJIS[thematiqueName] || THEMATIC_EMOJIS.Autre;
        BLOCK_TITLES[key] = `${emoji} ${thematiqueName}`;
      });

      return result;
    };

    const getData = async () => {
      if (applyFilters.value && !page.loading) {
        page.loading = true;
        download.percentCompleted = 0;
        download.running = true;

        try {
          const selectedSites = store.getters["aiFactory/getSelectedSites"];

          const { data } = await axiosPost(
            "/news-radar-services/angles_finder",
            {
              site_id: selectedSites,
              theme: searchStr.value,
              force_refresh: page.forceRefresh,
            },
            {
              skipErrorSnackbar: true,
              onDownloadProgress: (progressEvent) => {
                download.percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              },
            }
          );

          Object.keys(page.data).forEach((key) => {
            delete page.data[key];
          });
          Object.keys(page.selectedData).forEach((key) => {
            delete page.selectedData[key];
          });
          Object.keys(BLOCK_TITLES).forEach((key) => {
            delete BLOCK_TITLES[key];
          });

          const formattedData = formatData(data);
          Object.assign(page.data, formattedData);

          // Initialize selectedData with empty arrays for each module using set for reactivity
          Object.keys(page.data).forEach((module) => {
            set(page.selectedData, module, []);
          });
        } catch (error) {
          const detail = getErrorDetail(error);
          console.error("Error:", detail);
          snackbar.message = detail;
          snackbar.details = [];
          snackbar.actions = [];
          snackbar.color = "error";
          snackbar.show = true;
          resetDownload(download);
        }

        page.loading = false;
        page.submitting = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }
    };

    const setData = (module, selections) => {
      set(page.selectedData, module, selections);
    };

    const submit = async () => {
      const results = [];
      const selectedSites = store.getters["aiFactory/getSelectedSites"];

      const payload = {
        site_id: selectedSites,
        data: submitData.value,
        aif_custom_prompt_template_id: selectedTemplateId.value,
        user_email: localStorage.getItem("userEmail") || null,
        force_refresh: page.forceRefresh,
        image_handler: imageHandler.value,
      };

      try {
        // Démarrer le loader pour la soumission
        download.running = true;
        download.percentCompleted = 0;
        page.submitting = true;

        const response = await axiosPost(
          "/news-radar-services/angles_finder/get_sources_and_add_to_post_queue",
          payload,
          {
            skipErrorSnackbar: true,
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total > 0) {
                download.percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              }
            },
          }
        );
        if (response.data.success && response.data.success.length > 0) {
          response.data.success.forEach((topic) => {
            results.push({
              topic: topic,
              status: "success",
            });
          });
        }
        // `errors` porte la cause de chaque echec ; un back plus ancien ne
        // renvoie que `failed` (titres seuls), d'ou le repli sans cause.
        const errorByTopic = new Map(
          (response.data.errors || []).map((e) => [e.topic, e.error])
        );
        if (response.data.failed && response.data.failed.length > 0) {
          response.data.failed.forEach((topic) => {
            results.push({
              topic: topic,
              status: "error",
              error: errorByTopic.get(topic) || null,
            });
          });
        }
        // Construire un message récapitulatif
        const successCount = results.filter(
          (r) => r.status === "success"
        ).length;
        const errorCount = results.length - successCount;
        snackbar.message = `✅ ${successCount} brouillon(s) généré(s) avec succès · ❌ ${errorCount} en erreur`;
        snackbar.color = errorCount > 0 ? "error" : "success";
        snackbar.details = results;
        snackbar.show = true;
        const query = Array.isArray(selectedSites)
          ? selectedSites.length && selectedSites[0] !== ""
            ? { site_id: selectedSites[0] }
            : {}
          : selectedSites != null && selectedSites !== ""
          ? { site_id: selectedSites }
          : {};
        snackbar.actions = [
          {
            text: "OK",
            onClick: () =>
              router.push({
                name: "ai-factory-post-generation-list",
                query,
              }),
          },
        ];
      } catch (error) {
        const detail = getErrorDetail(error);
        console.error("Error:", detail);
        snackbar.message = `❌ Échec de la génération — ${detail}`;
        snackbar.details = [];
        snackbar.actions = [];
        snackbar.color = "error";
        snackbar.show = true;
        resetDownload(download);
      }

      // Réinitialiser l'état dans tous les cas
      page.submitting = false;
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const searchStr = computed(() => store.getters["aiFactory/getKeywords"]);

    const submitData = computed(() => {
      const result = Object.keys(page.selectedData).reduce(
        (accumulator, module) => [...accumulator, ...page.selectedData[module]],
        []
      );
      return result;
    });

    const buttonDisabled = computed(
      () =>
        !Object.keys(page.data).some((k) => page.data[k].length > 0) ||
        submitData.value.length === 0
    );

    const selectedSiteId = computed(() =>
      Number(store.getters["aiFactory/getSelectedSites"] || 0)
    );

    const selectedTemplateId = computed(
      () =>
        Number(store.getters["aiFactory/getCustomPromptTemplate"] || 0) || null
    );

    const imageHandler = computed({
      get: () => store.getters["aiFactory/getGenerationImageHandler"],
      set: (value) =>
        store.dispatch("aiFactory/updateGenerationImageHandler", value),
    });

    // =>  WATCH
    watch(applyFilters, () => {
      getData();
    });

    watch(
      () => download.percentCompleted,
      (newValue) => {
        if (newValue >= 100) {
          setTimeout(() => {
            resetDownload(download);
          }, 500);
        }
      }
    );

    // =>  HOOKS
    onMounted(async () => {
      saveRmraView(route);
    });

    // =>  DATA
    return {
      imageHandler,
      snackbar,
      BLOCK_TITLES,
      buttonDisabled,
      download,
      page,
      selectedTemplateId,
      selectedSiteId,
      setData,
      stepsGetData: {
        mdiDatabaseArrowLeftOutline: "Traitement du prompt",
        // mdiNewspaperVariantOutline: "Recherche des sources",
        mdiMerge: "Agrégation des résultats",
        mdiCalculatorVariantOutline: "Recherche des angles par l'agent IA",
        mdiCardSearchOutline: "Analyse des propositions",
        mdiSendClockOutline: "Finalisation de l'envoi",
      },
      stepsSubmit: {
        mdiDatabaseArrowLeftOutline: "Préparation de la génération",
        mdiBookOpenVariant: "Génération des articles de référence",
        mdiBrain: "Analyse des articles",
        mdiMerge: "Recherche des sources pertinentes",
        mdiCalculatorVariantOutline: "Génération des brouillons",
        mdiCardSearchOutline: "Validation des contenus",
        mdiSendClockOutline: "Envoi à la file d'attente",
      },
      submit,
      submitData,
    };
  },
};
</script>

<style></style>
